import { LGraphNode, LiteGraph } from "litegraph.js";

/**
 * RecorderNode
 * ------------
 * Records canvas frames to an internal buffer, then exports as
 * PNG sequence (zip), WebM video, or GIF from the same recording.
 *
 * Record once → download in any format.
 */
export class RecorderNode extends LGraphNode {
  constructor() {
    super();
    this.title = "Recorder";

    this.properties = {
      gifSize: 500,
    };

    // State
    this._frames = [];       // ImageData buffers
    this._frameW = 0;
    this._frameH = 0;
    this._recording = false;
    this._processing = false;
    this._status = "Ready";
    this._lastFrame = null;

    // Progress (0–1)
    this._progress = 0;
    this._progressTarget = 0;

    // Animation
    this._pulsePhase = 0;
    this._btnScale = 1;

    // Capture canvas (reused)
    this._captureCanvas = document.createElement("canvas");
    this._captureCtx = this._captureCanvas.getContext("2d", { willReadFrequently: true });

    // Input
    this.addInput("Canvas", "canvas");

    // Widgets — only gif size, export buttons are custom drawn
    this.addWidget("number", "GIF Size", this.properties.gifSize, (val) => {
      this.properties.gifSize = Math.max(100, Math.min(2000, Math.round(val)));
    }, { min: 100, max: 2000, step: 50 });

    this.size = [220, 180];
    this.mode = LiteGraph.ALWAYS;
    this.color = "#2a2a2a";
    this.bgcolor = "#333333";
  }

  // ─── Layout ───────────────────────────────────────────────────────
  _getRecordRect() {
    const w = this.size[0];
    return { cx: w / 2, cy: 80, r: 18 };
  }

  _getExportButtons() {
    const w = this.size[0];
    const y = 116;
    const gap = 6;
    const btnW = (w - 20 - gap * 2) / 3;
    return [
      { x: 10,              y, w: btnW, h: 22, label: "PNG", fmt: "png" },
      { x: 10 + btnW + gap, y, w: btnW, h: 22, label: "WebM", fmt: "webm" },
      { x: 10 + (btnW + gap) * 2, y, w: btnW, h: 22, label: "GIF", fmt: "gif" },
    ];
  }

  // ─── Recording ────────────────────────────────────────────────────
  _startRecording() {
    const input = this.getInputData(0);
    if (!input) {
      this._status = "No input!";
      return;
    }

    this._frames = [];
    this._frameW = input.width;
    this._frameH = input.height;
    this._captureCanvas.width = input.width;
    this._captureCanvas.height = input.height;
    this._recording = true;
    this._processing = false;
    this._progress = 0;
    this._progressTarget = 0;
    this._status = "Recording...";
    this._btnScale = 0.8;
  }

  _stopRecording() {
    this._recording = false;
    this._btnScale = 0.85;
    const n = this._frames.length;
    const secs = (n / 60).toFixed(1);
    this._status = `${n} frames (${secs}s) — pick format`;
  }

  // ─── Export ───────────────────────────────────────────────────────
  async _exportPng() {
    if (this._frames.length === 0 || this._processing) return;
    this._processing = true;
    this._progressTarget = 0;
    this._progress = 0;
    this._status = "Building PNG zip...";

    const { default: JSZip } = await import("jszip");
    const zip = new JSZip();
    const canvas = document.createElement("canvas");
    canvas.width = this._frameW;
    canvas.height = this._frameH;
    const ctx = canvas.getContext("2d");

    for (let i = 0; i < this._frames.length; i++) {
      ctx.putImageData(this._frames[i], 0, 0);
      const blob = await new Promise(res => canvas.toBlob(res, "image/png"));
      zip.file(`frame_${String(i).padStart(5, "0")}.png`, blob);
      this._progressTarget = (i + 1) / this._frames.length * 0.8;
      this._status = `Encoding ${i + 1}/${this._frames.length}`;
    }

    this._status = "Compressing zip...";
    const blob = await zip.generateAsync({ type: "blob" }, (meta) => {
      this._progressTarget = 0.8 + (meta.percent / 100) * 0.2;
      this._status = `Compressing ${Math.round(meta.percent)}%`;
    });

    this._download(blob, "frames.zip");
    this._progressTarget = 1;
    this._status = `Saved ${this._frames.length} PNGs`;
    this._processing = false;
  }

  async _exportWebm() {
    if (this._frames.length === 0 || this._processing) return;
    this._processing = true;
    this._progressTarget = 0;
    this._progress = 0;
    this._status = "Encoding WebM...";

    const canvas = document.createElement("canvas");
    canvas.width = this._frameW;
    canvas.height = this._frameH;
    const ctx = canvas.getContext("2d");

    const stream = canvas.captureStream(0);
    const recorder = new MediaRecorder(stream, {
      mimeType: "video/webm; codecs=vp9",
    });
    const blobs = [];
    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) blobs.push(e.data);
    };

    await new Promise((resolve) => {
      recorder.onstop = resolve;
      recorder.start();

      let i = 0;
      const writeFrame = () => {
        if (i >= this._frames.length) {
          recorder.stop();
          return;
        }
        ctx.putImageData(this._frames[i], 0, 0);
        const track = stream.getVideoTracks()[0];
        if (track.requestFrame) track.requestFrame();
        i++;
        this._progressTarget = i / this._frames.length;
        this._status = `Encoding ${i}/${this._frames.length}`;
        setTimeout(writeFrame, 1000 / 60);
      };
      writeFrame();
    });

    const videoBlob = new Blob(blobs, { type: "video/webm" });
    this._download(videoBlob, "recording.webm");
    this._progressTarget = 1;
    this._status = `Saved WebM (${this._frames.length} frames)`;
    this._processing = false;
  }

  async _exportGif() {
    if (this._frames.length === 0 || this._processing) return;
    this._processing = true;
    this._progressTarget = 0;
    this._progress = 0;
    this._status = "Building GIF...";

    const { default: GIF } = await import("gif.js.optimized");
    const gs = this.properties.gifSize;
    const canvas = document.createElement("canvas");
    canvas.width = gs;
    canvas.height = gs;
    const ctx = canvas.getContext("2d");

    const srcCanvas = document.createElement("canvas");
    srcCanvas.width = this._frameW;
    srcCanvas.height = this._frameH;
    const srcCtx = srcCanvas.getContext("2d");

    const gif = new GIF({
      workers: 2,
      workerScript: "/js/gif.worker.js",
      quality: 10,
      width: gs,
      height: gs,
      transparent: "0x000000",
    });

    // Add all frames
    for (let i = 0; i < this._frames.length; i++) {
      srcCtx.putImageData(this._frames[i], 0, 0);
      ctx.clearRect(0, 0, gs, gs);
      ctx.drawImage(srcCanvas, 0, 0, gs, gs);
      gif.addFrame(canvas, { delay: 1000 / 60, copy: true });
      this._progressTarget = (i + 1) / this._frames.length * 0.3;
      this._status = `Adding frames ${i + 1}/${this._frames.length}`;
    }

    // Render
    this._status = "Rendering GIF...";
    gif.on("progress", (p) => {
      this._progressTarget = 0.3 + p * 0.7;
      this._status = `Rendering GIF ${Math.round(p * 100)}%`;
    });

    await new Promise((resolve) => {
      gif.on("finished", (blob) => {
        this._download(blob, "animation.gif");
        resolve();
      });
      gif.render();
    });

    this._progressTarget = 1;
    this._status = `Saved GIF (${this._frames.length} frames)`;
    this._processing = false;
  }

  _download(blob, filename) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  // ─── Click handling ───────────────────────────────────────────────
  onMouseDown(e, localPos) {
    const x = localPos[0];
    const y = localPos[1];

    // Record button
    if (!this._processing) {
      const r = this._getRecordRect();
      const dx = x - r.cx, dy = y - r.cy;
      if (dx * dx + dy * dy <= (r.r + 4) * (r.r + 4)) {
        if (this._recording) this._stopRecording();
        else this._startRecording();
        return true;
      }
    }

    // Export buttons (only when we have frames and not recording/processing)
    if (this._frames.length > 0 && !this._recording && !this._processing) {
      for (const btn of this._getExportButtons()) {
        if (x >= btn.x && x <= btn.x + btn.w && y >= btn.y && y <= btn.y + btn.h) {
          if (btn.fmt === "png") this._exportPng();
          else if (btn.fmt === "webm") this._exportWebm();
          else if (btn.fmt === "gif") this._exportGif();
          return true;
        }
      }
    }

    return false;
  }

  // ─── Drawing ──────────────────────────────────────────────────────
  onDrawForeground(ctx) {
    const w = this.size[0];
    const h = this.size[1];

    this._pulsePhase += 0.06;
    this._btnScale += (1 - this._btnScale) * 0.15;
    this._progress += (this._progressTarget - this._progress) * 0.12;

    const r = this._getRecordRect();
    const hasFrames = this._frames.length > 0;

    // ── Record button ──
    ctx.save();
    ctx.translate(r.cx, r.cy);
    ctx.scale(this._btnScale, this._btnScale);

    if (this._recording) {
      // Pulsing glow
      const pulse = 0.3 + Math.sin(this._pulsePhase) * 0.15;
      ctx.beginPath();
      ctx.arc(0, 0, r.r + 4, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(255,60,60,${pulse})`;
      ctx.lineWidth = 3;
      ctx.stroke();

      // Ring
      ctx.beginPath();
      ctx.arc(0, 0, r.r, 0, Math.PI * 2);
      ctx.strokeStyle = "rgba(255,100,100,0.6)";
      ctx.lineWidth = 2.5;
      ctx.stroke();

      // Stop square
      const sq = 10;
      ctx.fillStyle = "#ff4444";
      this._roundRect(ctx, -sq, -sq, sq * 2, sq * 2, 3);
      ctx.fill();

    } else if (this._processing) {
      // Progress ring
      ctx.beginPath();
      ctx.arc(0, 0, r.r, 0, Math.PI * 2);
      ctx.strokeStyle = "rgba(255,255,255,0.1)";
      ctx.lineWidth = 3;
      ctx.stroke();

      ctx.beginPath();
      ctx.arc(0, 0, r.r, -Math.PI / 2, this._progress * Math.PI * 2 - Math.PI / 2);
      ctx.strokeStyle = "#ff8844";
      ctx.lineWidth = 3;
      ctx.lineCap = "round";
      ctx.stroke();
      ctx.lineCap = "butt";

      ctx.fillStyle = "#ffaa66";
      ctx.font = "bold 12px sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(`${Math.round(this._progress * 100)}%`, 0, 0);
      ctx.textAlign = "left";
      ctx.textBaseline = "alphabetic";

    } else {
      // Idle — ring
      ctx.beginPath();
      ctx.arc(0, 0, r.r, 0, Math.PI * 2);
      ctx.strokeStyle = "rgba(255,255,255,0.35)";
      ctx.lineWidth = 2.5;
      ctx.stroke();

      // Record dot
      ctx.beginPath();
      ctx.arc(0, 0, r.r - 6, 0, Math.PI * 2);
      ctx.fillStyle = "#e04040";
      ctx.fill();
    }

    ctx.restore();

    // ── Recording info ──
    if (this._recording) {
      const dotAlpha = 0.5 + Math.sin(this._pulsePhase * 2) * 0.5;
      ctx.fillStyle = `rgba(255,50,50,${dotAlpha})`;
      ctx.beginPath();
      ctx.arc(18, r.cy - 6, 3, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = "#ff8888";
      ctx.font = "bold 10px monospace";
      ctx.fillText(`${this._frames.length}`, 24, r.cy - 2);

      const secs = (this._frames.length / 60).toFixed(1);
      ctx.fillStyle = "rgba(255,255,255,0.3)";
      ctx.font = "9px sans-serif";
      ctx.textAlign = "right";
      ctx.fillText(`${secs}s`, w - 14, r.cy - 2);
      ctx.textAlign = "left";
    }

    // ── Export buttons ──
    const btns = this._getExportButtons();
    for (const btn of btns) {
      const enabled = hasFrames && !this._recording && !this._processing;
      const alpha = enabled ? 1 : 0.25;

      ctx.fillStyle = enabled ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.03)";
      this._roundRect(ctx, btn.x, btn.y, btn.w, btn.h, 4);
      ctx.fill();

      ctx.strokeStyle = `rgba(255,255,255,${enabled ? 0.2 : 0.06})`;
      ctx.lineWidth = 1;
      this._roundRect(ctx, btn.x, btn.y, btn.w, btn.h, 4);
      ctx.stroke();

      ctx.fillStyle = `rgba(255,255,255,${alpha * 0.7})`;
      ctx.font = "bold 9px sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(btn.label, btn.x + btn.w / 2, btn.y + 14);
      ctx.textAlign = "left";
    }

    // ── Progress bar (processing) ──
    if (this._processing || this._progress > 0.01) {
      const barY = 146;
      const barH = 6;
      const barX = 14;
      const barW = w - 28;

      ctx.fillStyle = "rgba(0,0,0,0.3)";
      this._roundRect(ctx, barX, barY, barW, barH, 3);
      ctx.fill();

      const fillW = Math.max(barH, barW * this._progress);
      const grad = ctx.createLinearGradient(barX, 0, barX + barW, 0);
      grad.addColorStop(0, "#ff6633");
      grad.addColorStop(1, "#ffaa44");
      ctx.fillStyle = grad;
      this._roundRect(ctx, barX, barY, fillW, barH, 3);
      ctx.fill();
    }

    // ── Frame count badge ──
    if (hasFrames && !this._recording) {
      const secs = (this._frames.length / 60).toFixed(1);
      ctx.fillStyle = "rgba(255,255,255,0.25)";
      ctx.font = "9px sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(`${this._frames.length} frames / ${secs}s`, w / 2, r.cy + r.r + 14);
      ctx.textAlign = "left";
    }

    // ── Status ──
    const statusColor = this._recording
      ? "rgba(255,130,130,0.7)"
      : this._processing
        ? "rgba(255,170,100,0.7)"
        : "rgba(255,255,255,0.5)";
    ctx.fillStyle = statusColor;
    ctx.font = "9px sans-serif";
    ctx.fillText(this._status, 10, h - 6);
  }

  _roundRect(ctx, x, y, w, h, r) {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
  }

  onExecute() {
    const input = this.getInputData(0);
    if (!input) return;

    this._lastFrame = input;

    if (!this._recording) return;

    // Sync capture canvas size
    if (this._captureCanvas.width !== input.width || this._captureCanvas.height !== input.height) {
      this._captureCanvas.width = input.width;
      this._captureCanvas.height = input.height;
      this._frameW = input.width;
      this._frameH = input.height;
    }

    // Capture frame as ImageData
    this._captureCtx.clearRect(0, 0, input.width, input.height);
    this._captureCtx.drawImage(input, 0, 0);
    this._frames.push(this._captureCtx.getImageData(0, 0, input.width, input.height));
  }
}

LiteGraph.registerNodeType("export/Recorder", RecorderNode);
